import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
    Content,
    UserRoleEnum,
    useGetClientsQuery,
    useGetLibrariesLazyQuery,
    useGetContentsLazyQuery,
    useSetRelatedContentsMutation,
    useDeleteRelatedContentsMutation,
} from '../../../generated/graphql';
import ShowOnRole from '../ShowOnRole/ShowOnRole';
import ContentDetailDialog from '../../dialogs/ContentDetailDialog/ContentDetailDialog';
import { Search, X, Plus, Trash2, Monitor, Box, ChevronDown, Building2, Library, ExternalLink } from 'lucide-react';

/* ─── Software-icon mapping ──────────────────────────────────── */

const SOFTWARE_ICONS: Record<string, string> = {
    revit: '🏗️',
    autocad: '📐',
    'civil 3d': '🛣️',
    navisworks: '🔍',
    archicad: '🏛️',
    tekla: '🔩',
    ifc: '📦',
    rhino: '🦏',
    sketchup: '📏',
    blender: '🎨',
    vectorworks: '✏️',
    pdf: '📄',
};

/** Derive a software label from the content's "Software" tag or applicationName */
function getSoftwareLabel(item: Partial<Content>): string | null {
    const softwareTag = item.tags?.find(t => t?.type?.toLowerCase() === 'software');
    if (softwareTag?.name) return softwareTag.name;
    if (item.applicationName) return item.applicationName;
    return null;
}

function getSoftwareIcon(label: string): string {
    return SOFTWARE_ICONS[label.toLowerCase()] ?? '💻';
}

/* ─── Styled dropdown ────────────────────────────────────────── */

type DropdownOption = { id: string; name: string };

type SelectDropdownProps = {
    icon: React.ReactNode;
    placeholder: string;
    value: string | null;
    options: DropdownOption[];
    loading?: boolean;
    disabled?: boolean;
    onChange: (id: string, name: string) => void;
};

const SelectDropdown = ({ icon, placeholder, value, options, loading, disabled, onChange }: SelectDropdownProps) => {
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered = options.filter(o => o.name.toLowerCase().includes(filter.toLowerCase()));

    return (
        <div ref={ref} className="relative flex-1 min-w-0">
            <button
                disabled={disabled}
                onClick={() => { setOpen(prev => !prev); setFilter(''); }}
                className="w-full flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm text-left"
                style={{
                    border: '1px solid var(--border-default)',
                    background: 'var(--bg-input)',
                    color: value ? 'var(--text-primary)' : 'var(--text-tertiary)',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.5 : 1,
                }}
            >
                {icon}
                <span className="flex-1 truncate">{value || placeholder}</span>
                <ChevronDown size={12} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
            </button>
            {open && (
                <div
                    className="absolute z-50 mt-1 w-full rounded-lg shadow-lg overflow-hidden animate-fade-in"
                    style={{
                        border: '1px solid var(--border-default)',
                        background: 'var(--bg-card)',
                        maxHeight: 220,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <div className="px-2 py-1.5" style={{ borderBottom: '1px solid var(--border-default)' }}>
                        <input
                            autoFocus
                            className="w-full text-xs bg-transparent outline-none"
                            style={{ border: 'none', color: 'var(--text-primary)' }}
                            placeholder="Filter…"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        />
                    </div>
                    <div className="overflow-y-auto flex-1">
                        {loading && (
                            <div className="text-xs py-3 text-center" style={{ color: 'var(--text-tertiary)' }}>Loading…</div>
                        )}
                        {!loading && filtered.length === 0 && (
                            <div className="text-xs py-3 text-center" style={{ color: 'var(--text-tertiary)' }}>No results</div>
                        )}
                        {filtered.map(o => (
                            <button
                                key={o.id}
                                className="w-full text-left text-sm px-3 py-1.5 hover:bg-opacity-10"
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'var(--text-primary)',
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
                                onMouseOut={(e) => { e.currentTarget.style.background = 'none'; }}
                                onClick={() => { onChange(o.id, o.name); setOpen(false); setFilter(''); }}
                            >
                                {o.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

/* ─── Component ──────────────────────────────────────────────── */

type RelatedContentsPanelProps = {
    contentId: string;
    relatedContents: Partial<Content>[];
    isAdmin: boolean;
    onRefetch: () => void;
};

const RelatedContentsPanel = ({ contentId, relatedContents, isAdmin, onRefetch }: RelatedContentsPanelProps) => {
    const [showSearch, setShowSearch] = useState(false);
    const [removingId, setRemovingId] = useState<string | null>(null);

    // Nested content detail dialog
    const [nestedContentId, setNestedContentId] = useState<string | null>(null);
    const [nestedClientId, setNestedClientId] = useState<string | null>(null);

    // Client / library / search state
    const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
    const [selectedClientName, setSelectedClientName] = useState<string | null>(null);
    const [selectedLibraryId, setSelectedLibraryId] = useState<string | null>(null);
    const [selectedLibraryName, setSelectedLibraryName] = useState<string | null>(null);
    const [searchText, setSearchText] = useState('');
    const searchInputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();

    // Queries & mutations
    const clientsResult = useGetClientsQuery({ variables: { contains: '' } });
    const [fetchLibraries, librariesResult] = useGetLibrariesLazyQuery();
    const [fetchContents, contentsResult] = useGetContentsLazyQuery();
    const [setRelatedContents] = useSetRelatedContentsMutation();
    const [deleteRelatedContents] = useDeleteRelatedContentsMutation();

    // When client changes, fetch libraries
    const handleClientChange = (id: string, name: string) => {
        setSelectedClientId(id);
        setSelectedClientName(name);
        setSelectedLibraryId(null);
        setSelectedLibraryName(null);
        setSearchText('');
        fetchLibraries({ variables: { clientId: id, contains: '' } });
    };

    // When library changes, reset search
    const handleLibraryChange = (id: string, name: string) => {
        setSelectedLibraryId(id);
        setSelectedLibraryName(name);
        setSearchText('');
    };

    // Focus search input when library is selected
    useEffect(() => {
        if (selectedLibraryId && showSearch) searchInputRef.current?.focus();
    }, [selectedLibraryId, showSearch]);

    // Debounced content search within the selected library
    const handleSearchChange = useCallback((value: string) => {
        setSearchText(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            if (value.trim().length >= 2 && selectedLibraryId) {
                fetchContents({
                    variables: {
                        libraryId: selectedLibraryId,
                        contains: value.trim(),
                        category: value.trim(),
                    },
                });
            }
        }, 300);
    }, [fetchContents, selectedLibraryId]);

    const existingIds = new Set((relatedContents || []).map(c => c.id).filter(Boolean));

    const searchResults = (contentsResult.data?.Library?.contents?.nodes || [])
        .filter(Boolean)
        .filter(c => c!.id !== contentId && !existingIds.has(c!.id));

    const clientOptions: DropdownOption[] = (clientsResult.data?.ClientList?.nodes || [])
        .filter(Boolean)
        .map(c => ({ id: c!.id!, name: c!.name || '' }));

    const libraryOptions: DropdownOption[] = (librariesResult.data?.LibraryList?.nodes || [])
        .filter(Boolean)
        .map(l => ({ id: l!.id!, name: l!.name || '' }));

    const handleAdd = async (targetId: string) => {
        await setRelatedContents({ variables: { id: contentId, ids: [targetId] } });
        onRefetch();
    };

    const handleRemove = async (targetId: string) => {
        setRemovingId(targetId);
        // Remove from both sides — the link may have been stored on either content.
        // One call removes the forward link (this → target), the other removes
        // the reverse link (target → this). One may no-op; that's fine.
        await Promise.allSettled([
            deleteRelatedContents({ variables: { id: contentId, ids: [targetId] } }),
            deleteRelatedContents({ variables: { id: targetId, ids: [contentId] } }),
        ]);
        setRemovingId(null);
        onRefetch();
    };

    const handleToggleSearch = () => {
        setShowSearch(prev => {
            if (prev) {
                // Reset when closing
                setSelectedClientId(null);
                setSelectedClientName(null);
                setSelectedLibraryId(null);
                setSelectedLibraryName(null);
                setSearchText('');
            }
            return !prev;
        });
    };

    return (
        <div className="flex flex-col h-full">
            {/* Toolbar */}
            <div
                className="flex items-center gap-2 px-4 py-2.5 border-b"
                style={{ borderColor: 'var(--border-default)' }}
            >
                <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                    <button
                        className="btn btn-ghost text-sm"
                        onClick={handleToggleSearch}
                    >
                        {showSearch ? <X size={15} /> : <Plus size={15} />}
                        {showSearch ? 'Cancel' : 'Add Related'}
                    </button>
                </ShowOnRole>
                <span className="text-xs ml-auto" style={{ color: 'var(--text-tertiary)' }}>
                    {relatedContents.length} item{relatedContents.length !== 1 ? 's' : ''}
                </span>
            </div>

            {/* Search panel (admin only) */}
            {showSearch && (
                <div
                    className="px-4 py-3 border-b animate-fade-in flex flex-col gap-2.5"
                    style={{ borderColor: 'var(--border-default)', background: 'var(--bg-subtle)' }}
                >
                    {/* Client & Library dropdowns */}
                    <div className="flex gap-2">
                        <SelectDropdown
                            icon={<Building2 size={13} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />}
                            placeholder="Select client…"
                            value={selectedClientName}
                            options={clientOptions}
                            loading={clientsResult.loading}
                            onChange={handleClientChange}
                        />
                        <SelectDropdown
                            icon={<Library size={13} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />}
                            placeholder="Select library…"
                            value={selectedLibraryName}
                            options={libraryOptions}
                            loading={librariesResult.loading}
                            disabled={!selectedClientId}
                            onChange={handleLibraryChange}
                        />
                    </div>

                    {/* Content search input */}
                    <div
                        className="flex items-center gap-2 rounded-lg px-3 py-1.5"
                        style={{
                            border: '1px solid var(--border-default)',
                            background: selectedLibraryId ? 'var(--bg-input)' : 'var(--bg-subtle)',
                            opacity: selectedLibraryId ? 1 : 0.5,
                        }}
                    >
                        <Search size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                        <input
                            ref={searchInputRef}
                            disabled={!selectedLibraryId}
                            className="flex-1 text-sm bg-transparent outline-none"
                            style={{ border: 'none', color: 'var(--text-primary)' }}
                            placeholder={selectedLibraryId ? 'Search content by name or category…' : 'Select a client and library first'}
                            value={searchText}
                            onChange={(e) => handleSearchChange(e.target.value)}
                        />
                        {searchText && (
                            <button
                                className="p-0.5 rounded hover:opacity-70"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}
                                onClick={() => setSearchText('')}
                            >
                                <X size={12} />
                            </button>
                        )}
                    </div>

                    {/* Search results */}
                    {selectedLibraryId && searchText.trim().length >= 2 && (
                        <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
                            {contentsResult.loading && (
                                <span className="text-xs py-2 text-center" style={{ color: 'var(--text-tertiary)' }}>
                                    Searching…
                                </span>
                            )}
                            {!contentsResult.loading && searchResults.length === 0 && (
                                <span className="text-xs py-2 text-center" style={{ color: 'var(--text-tertiary)' }}>
                                    No results found
                                </span>
                            )}
                            {searchResults.map(item => {
                                const sw = getSoftwareLabel(item as Partial<Content>);
                                return (
                                    <button
                                        key={item!.id}
                                        className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-left group"
                                        style={{
                                            background: 'var(--bg-card)',
                                            border: '1px solid var(--border-default)',
                                            cursor: 'pointer',
                                            transition: 'border-color 0.15s',
                                        }}
                                        onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
                                        onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border-default)'; }}
                                        onClick={() => handleAdd(item!.id!)}
                                    >
                                        {item!.thumbnail?.downloadUrl ? (
                                            <img
                                                src={item!.thumbnail.downloadUrl}
                                                alt=""
                                                className="rounded"
                                                style={{ width: 28, height: 28, objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <span
                                                className="flex items-center justify-center rounded"
                                                style={{ width: 28, height: 28, background: 'var(--accent-soft)', color: 'var(--accent)' }}
                                            >
                                                <Box size={14} />
                                            </span>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>
                                                {item!.name}
                                            </div>
                                            <div className="text-xs truncate" style={{ color: 'var(--text-tertiary)' }}>
                                                {item!.category}{item!.library?.name ? ` · ${item!.library.name}` : ''}
                                            </div>
                                        </div>
                                        {sw && (
                                            <span
                                                className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded"
                                                style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
                                                title={sw}
                                            >
                                                <span>{getSoftwareIcon(sw)}</span>
                                                <span>{sw}</span>
                                            </span>
                                        )}
                                        <Plus size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--accent)', flexShrink: 0 }} />
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Related items list */}
            <div className="flex-1 overflow-y-auto px-4 py-3">
                {relatedContents.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-2">
                        <Monitor size={32} style={{ color: 'var(--text-tertiary)', opacity: 0.4 }} />
                        <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                            No related content
                        </span>
                    </div>
                ) : (
                    <div className="flex flex-col gap-1.5">
                        {relatedContents.map(item => {
                            const sw = getSoftwareLabel(item);
                            return (
                                <div
                                    key={item.id}
                                    className="flex items-center gap-2.5 px-3 py-2 rounded-lg group"
                                    style={{
                                        background: 'var(--bg-card)',
                                        border: '1px solid var(--border-default)',
                                        opacity: removingId === item.id ? 0.5 : 1,
                                        transition: 'opacity 0.15s, border-color 0.15s',
                                        cursor: 'pointer',
                                    }}
                                    onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
                                    onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border-default)'; }}
                                    onClick={() => {
                                        if (item.id && item.library?.clientId) {
                                            setNestedContentId(item.id);
                                            setNestedClientId(item.library.clientId);
                                        }
                                    }}
                                >
                                    {/* Software icon badge */}
                                    {sw ? (
                                        <span
                                            className="flex items-center justify-center rounded-lg"
                                            style={{ width: 32, height: 32, background: 'var(--accent-soft)', fontSize: '16px' }}
                                            title={sw}
                                        >
                                            {getSoftwareIcon(sw)}
                                        </span>
                                    ) : item.thumbnail?.downloadUrl ? (
                                        <img
                                            src={item.thumbnail.downloadUrl}
                                            alt=""
                                            className="rounded-lg"
                                            style={{ width: 32, height: 32, objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <span
                                            className="flex items-center justify-center rounded-lg"
                                            style={{ width: 32, height: 32, background: 'var(--accent-soft)', color: 'var(--accent)' }}
                                        >
                                            <Box size={15} />
                                        </span>
                                    )}

                                    {/* Content info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                                            {item.name}
                                        </div>
                                        <div className="text-xs truncate" style={{ color: 'var(--text-tertiary)' }}>
                                            {item.category}
                                            {item.library?.name ? ` · ${item.library.name}` : ''}
                                            {sw ? ` · ${sw}` : ''}
                                        </div>
                                    </div>

                                    {/* Software tag pill */}
                                    {sw && (
                                        <span
                                            className="badge-accent inline-flex items-center gap-1 text-xs"
                                            style={{ flexShrink: 0 }}
                                        >
                                            <span>{getSoftwareIcon(sw)}</span>
                                            <span>{sw}</span>
                                        </span>
                                    )}

                                    {/* Remove button (admin only) */}
                                    <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                                        <button
                                            className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: 'var(--danger)',
                                                flexShrink: 0,
                                            }}
                                            title="Remove related content"
                                            onClick={(e) => { e.stopPropagation(); handleRemove(item.id!); }}
                                            disabled={removingId === item.id}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </ShowOnRole>

                                    {/* Open indicator */}
                                    <ExternalLink
                                        size={13}
                                        className="opacity-0 group-hover:opacity-60 transition-opacity"
                                        style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Nested Content Detail Dialog */}
            {nestedContentId && nestedClientId && (
                <ContentDetailDialog
                    visible={true}
                    contentId={nestedContentId}
                    clientId={nestedClientId}
                    isNested={true}
                    onClose={() => { setNestedContentId(null); setNestedClientId(null); onRefetch(); }}
                />
            )}
        </div>
    );
};

export default RelatedContentsPanel;
